import React, { useState } from "react";

import { Box, Button, CircularProgress, Divider, Grid, IconButton, Menu, MenuItem, Typography } from "@mui/material";
import { Add, Edit, MoreVert } from "@mui/icons-material";

import { DataLayout, FieldControl, StatusChip } from "../../../shared/components";
import { useDisplayField } from "../../../hooks/useDisplayField";
import { useCapabilities } from "../../../hooks/useCapabilities";

interface RaiseCauseProps {
    causesData: any;
    isCauseFetching: boolean;
    isCausePending: boolean;
    setCausesData: (data: any) => void;
    onClearCause: () => void;
    isSaving: boolean;
    onSaveCause: () => void;
    editCauseStatus: any;
    causeData: any;
    onEditCause: (cause: any) => void;
}

export const RaiseCause: React.FC<RaiseCauseProps> = ({ causesData, isCauseFetching, isCausePending, setCausesData, onClearCause, isSaving, onSaveCause, editCauseStatus, causeData, onEditCause }) => {

    const { can } = useCapabilities();

    const isAllowedAddCause = can("operational_delivery:project:risk:causes:add");
    const isAllowedEditCause = can("operational_delivery:project:risk:causes:edit");
    const [formStatus, setFormStatus] = useState<boolean>(false);

    const { getFilterOptions } = useDisplayField([]);

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selected, setSelected] = useState<any>(null);

    const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, cause: any) => {
        event.stopPropagation();
        setSelected(cause);
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    if (isCauseFetching || isCausePending) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box display={'flex'} flexDirection={'column'} height={'100%'} gap={2}>
            {
                !formStatus && isAllowedAddCause && (
                    <Button onClick={() => setFormStatus(true)} variant="outlined">Add Cause</Button>
                )
            }
            {
                formStatus && (
                    <DataLayout title={"Add Cause"} bgVariant="filled">
                        <Grid container spacing={2} p={2}>
                            <Grid size={6}>
                                <FieldControl
                                    disabled={isCauseFetching || isCausePending}
                                    keyName={"causeTitle"}
                                    label={"Title"}
                                    variant={"input"}
                                    value={causesData.causeTitle}
                                    onChange={(key: string, value: any) => {
                                        setCausesData({ ...causesData, [key]: value });
                                    }}
                                />
                            </Grid>
                            <Grid size={6}>
                                <FieldControl
                                    disabled={isCauseFetching || isCausePending}
                                    keyName={"causeStatus"}
                                    label={"Status"}
                                    variant={"select"}
                                    options={getFilterOptions('RISK_STATUS').map((value) => {
                                        return {
                                            label: value.display_text,
                                            value: value.key
                                        }
                                    })}
                                    value={causesData.causeStatus}
                                    onChange={(key: string, value: any) => {
                                        setCausesData({ ...causesData, [key]: value });
                                    }}
                                />
                            </Grid>
                            <Grid size={12}>
                                <FieldControl
                                    disabled={isCauseFetching || isCausePending}
                                    keyName={"causeDescription"}
                                    label={"Description"}
                                    variant={"input"}
                                    multiline
                                    value={causesData.causeDescription}
                                    onChange={(key: string, value: any) => {
                                        setCausesData({ ...causesData, [key]: value });
                                    }}
                                />
                            </Grid>
                            <Grid size={12}>
                                <Divider />
                            </Grid>
                            <Grid size={12} display={'flex'} justifyContent={'flex-end'} gap={2}>
                                <Button
                                    sx={{ color: 'text.primary' }}
                                    onClick={() => {
                                        onClearCause();
                                        setFormStatus(false);
                                    }}
                                    disabled={isSaving}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    startIcon={<Add />}
                                    onClick={onSaveCause}
                                    loading={isSaving}
                                    disabled={isSaving}
                                >
                                    {editCauseStatus.editCauseStatus ? 'Edit Cause' : 'Add Cause'}
                                </Button>
                            </Grid>
                        </Grid>
                    </DataLayout>
                )
            }
            <Box flex={1} display={'flex'} flexDirection={'column'} gap={2} sx={{ overflowY: 'auto' }}>
                {
                    causeData?.length === 0 ?
                        <Typography variant="body2" color="text.secondary" textAlign="center" mt={5}>No causes found.</Typography> :
                        causeData.map((cause: any) => (
                            <Box display={'flex'} gap={1} key={cause.cause_id} border={1} borderColor={'divider'} justifyContent={'space-between'} alignItems={'center'} borderRadius={1} padding={2}>
                                <Box display={'flex'} flexDirection={'column'} alignItems={'start'} gap={.5}>
                                    <Typography variant="subtitle2" color="text.primary">{cause.title}</Typography>
                                    <Typography variant="caption" color="text.secondary">{cause.description}</Typography>
                                </Box>
                                <Box display={'flex'} alignItems={'center'} gap={.5}>
                                    <StatusChip label={cause.status_display_text} color="amber" />
                                    {
                                        isAllowedEditCause && (
                                            <IconButton onClick={(e) => handleMenuOpen(e, cause)}>
                                                <MoreVert fontSize="small" />
                                            </IconButton>
                                        )
                                    }
                                </Box>
                                <Menu
                                    anchorEl={anchorEl}
                                    open={Boolean(anchorEl)}
                                    onClose={handleMenuClose}
                                >
                                    <MenuItem
                                        onClick={() => {
                                            onEditCause(selected);
                                            setFormStatus(true);
                                            handleMenuClose();
                                        }}
                                    >
                                        <Edit fontSize="small" />
                                        <Typography ml={1}>Edit</Typography>
                                    </MenuItem>
                                </Menu>
                            </Box>
                        ))
                }
            </Box>
        </Box>
    )
}